// Store AUTENTICAZIONE
import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import type { AppUser } from '@/types';
import {
  getCurrentUser, onAuthChange, isSupabaseConfigured,
  signInWithEmail, signUpWithEmail, signInWithGoogle,
  resendConfirmationEmail, resetPassword, signOut as sbSignOut,
  type AuthResult,
} from '@/lib/supabase';
import { fullSync, pullCloudToLocal } from '@/lib/sync';
import { useNotesStore } from './notesStore';
import { usePersonalTasksStore } from './personalTasksStore';
import { useProfitTasksStore } from './profitTasksStore';
import { useRemindersStore } from './remindersStore';

interface AuthState {
  user: AppUser | null;
  loading: boolean;
  initializing: boolean;
  initialized: boolean;
  init: () => Promise<void>;
  loginEmail: (email: string, password: string) => Promise<AuthResult>;
  registerEmail: (email: string, password: string) => Promise<AuthResult>;
  loginGoogle: () => Promise<void> | any;
  resendConfirm: (email: string) => Promise<AuthResult>;
  resetPwd: (email: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  setUser: (u: AppUser | null) => void;
  syncNow: () => Promise<AuthResult>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  initializing: false,
  initialized: false,

  init: async () => {
    if (get().initialized || get().initializing) return;
    if (!isSupabaseConfigured()) {
      set({ initialized: true });
      return;
    }
    set({ initializing: true });

    // -----------------------------------------------
    // ✅ Inizializzazione del plugin SocialLogin (una sola volta)
    //    Deve avvenire prima di qualsiasi tentativo di login.
    //    I client ID vengono letti da .env (variabili VITE_GOOGLE_*).
    //    Se i client ID non sono configurati, saltiamo l'inizializzazione:
    //    il login Google su mobile restituirà un errore chiaro, ma l'app
    //    continua a funzionare per login email/password.
    // -----------------------------------------------
    if (Capacitor.isNativePlatform()) {
      const webClientId = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined)?.trim();
      const androidClientId = (import.meta.env.VITE_GOOGLE_ANDROID_CLIENT_ID as string | undefined)?.trim();
      const iosClientId = (import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID as string | undefined)?.trim();

      // 🔍 LOG DIAGNOSTICO: mostra cosa sta leggendo dal .env
      // (le variabili VITE_ vengono embedded al build time: se vedi qui
      // "undefined" significa che il .env non era presente prima di `npm run build`)
      console.log('[auth] Google OAuth env check:', {
        platform: Capacitor.getPlatform(),
        webClientId: webClientId ? `${webClientId.slice(0, 12)}...` : 'MISSING',
        androidClientId: androidClientId ? `${androidClientId.slice(0, 12)}...` : 'MISSING',
        iosClientId: iosClientId ? `${iosClientId.slice(0, 12)}...` : 'MISSING',
      });

      if (!webClientId) {
        console.error(
          '[auth] VITE_GOOGLE_WEB_CLIENT_ID mancante in .env — il login Google su mobile non funzionerà. ' +
          'Vedi .env.example per la configurazione.'
        );
      } else {
        try {
          // Costruisci la config Google per @capgo/capacitor-social-login v8.
          // In v8 il plugin usa Credential Manager API (non più GoogleSignInClient).
          // Per Credential Manager il parametro CHIAVE è `webClientId`: è l'audience
          // dell'idToken emesso da Google, e DEVE corrispondere al Client ID
          // configurato in Supabase → Authentication → Providers → Google.
          // `androidClientId` è opzionale (abilita solo One Tap UI): se lo passi
          // assicurati che il suo SHA-1 in Google Cloud Console sia corretto,
          // altrimenti il provider viene scartato silenziosamente.
          const googleConfig: Record<string, string | boolean> = {
            webClientId,
            forceAuthCode: false,
          };
          if (androidClientId) googleConfig.androidClientId = androidClientId;
          if (iosClientId) googleConfig.iosClientId = iosClientId;

          console.log('[auth] Chiamata SocialLogin.initialize() con:', { googleConfig });
          await SocialLogin.initialize({
            google: googleConfig,
          } as any);
          console.log('[auth] ✅ SocialLogin inizializzato (Google)');

          // Verifica post-init: il plugin espone un metodo per controllare lo stato?
          // Proviamo a chiamare isLoggedIn() (se esiste) come sanity check.
          try {
            // Alcune versioni del plugin espongono getCurrentUser() o isGoogleAvailable()
            // Se non esiste, ignoriamo l'errore silenziosamente.
          } catch {
            /* ignore */
          }
        } catch (e: any) {
          console.error('[auth] ❌ Errore inizializzazione SocialLogin:', e?.message ?? e);
          console.error('[auth] Stack:', e?.stack);
        }
      }
    }
    // -----------------------------------------------

    try {
      const u = await getCurrentUser();
      set({ user: u, initializing: false, initialized: true });
      // Sync iniziale (pull) se loggato
      if (u) {
        try {
          await pullCloudToLocal(u);
          await Promise.all([
            useNotesStore.getState().load(),
            usePersonalTasksStore.getState().load(),
            useProfitTasksStore.getState().load(),
            useRemindersStore.getState().load(),
          ]);
        } catch (e) {
          console.warn('[auth] sync iniziale fallito (offline?)', e);
        }
      }
      // Sottoscrivi cambi auth
      onAuthChange((newUser) => {
        set({ user: newUser });
        if (newUser) {
          // Pull dopo login
          pullCloudToLocal(newUser)
            .then(() => {
              return Promise.all([
                useNotesStore.getState().load(),
                usePersonalTasksStore.getState().load(),
                useProfitTasksStore.getState().load(),
                useRemindersStore.getState().load(),
              ]);
            })
            .catch((e) => console.warn('[auth] pull post-login fallito', e));
        }
      });
    } catch (e) {
      console.error('[auth] init error', e);
      set({ initializing: false, initialized: true });
    }
  },

  loginEmail: async (email, password) => {
    set({ loading: true });
    try {
      const r = await signInWithEmail(email, password);
      if (r.ok && r.user) set({ user: r.user });
      return r;
    } finally {
      set({ loading: false });
    }
  },

  registerEmail: async (email, password) => {
    set({ loading: true });
    try {
      return await signUpWithEmail(email, password);
    } finally {
      set({ loading: false });
    }
  },

  loginGoogle: async () => {
    // Il plugin è già stato inizializzato in init()
    await signInWithGoogle();
    // Su web: redirect OAuth
    // Su mobile: se non vengono lanciate eccezioni, il login è riuscito
    //   e l'aggiornamento dello stato auth avverrà tramite onAuthChange
  },

  resendConfirm: async (email) => resendConfirmationEmail(email),
  resetPwd: async (email) => resetPassword(email),

  logout: async () => {
    const u = get().user;
    if (u) {
      try {
        await fullSync(u);
      } catch {
        /* ignore */
      }
    }
    await sbSignOut();
    set({ user: null });
  },

  setUser: (u) => set({ user: u }),

  syncNow: async () => {
    const u = get().user;
    if (!u) return { ok: false, message: 'Non autenticato' };
    try {
      const r = await fullSync(u);
      await Promise.all([
        useNotesStore.getState().load(),
        usePersonalTasksStore.getState().load(),
        useProfitTasksStore.getState().load(),
        useRemindersStore.getState().load(),
      ]);
      return r;
    } catch (e: any) {
      return { ok: false, message: e?.message ?? 'Errore sync' };
    }
  },
}));